function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useState, useCallback, useContext, useEffect } from 'react';
import cn from 'clsx';
import { shape, string, number, bool, oneOfType, func, object } from 'prop-types';
import fetch from 'isomorphic-unfetch';
import ResizeObserver from 'resize-observer-polyfill';
import { useTranslation } from 'react-i18next';
import DashedButton from '../../atoms/DashedButton/index.jsx';
import Button from '../Button/index.jsx';
import Icon from '../../atoms/Icon/index.jsx';
import IconStack from '../IconStack/index.jsx';
import FormRange from '../../atoms/FormRange/index.jsx';
import FormContext from '../../context/FormContext.js';
import FormGroupContext from '../../context/FormGroupContext.js';
import { imageSizes } from '../../atoms/Image/sizes.js';
import { readAsDataURL } from '../../utils/index.js';
const getDefaultImgData = defaultValue => {
  if (typeof defaultValue === 'object') {
    return defaultValue;
  }
  let defaultImgData;
  try {
    defaultImgData = JSON.parse(defaultValue || '{}');
  } catch (err) {
    return {};
  }
  return defaultImgData;
};

/**
 * FormImageUpload component.
 *
 * Is used to add an upload component to a page allows for custom dimensions,
 * filetypes and filesizes.
 */
var _ref = /*#__PURE__*/React.createElement(IconStack, null, /*#__PURE__*/React.createElement(Icon, {
  key: "1",
  name: "cross",
  color: "baseGrey"
}), /*#__PURE__*/React.createElement(Icon, {
  key: "2",
  name: "circle",
  color: "baseWhite",
  size: 30
}));
const FormImageUpload = ({
  className,
  name,
  accept,
  required,
  minDimensions,
  maxFileSize,
  aspectRatio,
  defaultValue,
  reactEasyCrop: easyCrop,
  ...restProps
}) => {
  var _imageData$crop, _imageData$crop2, _imageData$crop3, _imageData$crop4, _imageData$crop5, _imageData$crop6;
  const {
    inputId,
    feedbackId
  } = useContext(FormGroupContext);
  const {
    errors,
    inverted,
    defaultValues,
    validateInput,
    reactEasyCrop = easyCrop
  } = useContext(FormContext);
  const {
    t
  } = useTranslation();
  if (!reactEasyCrop) {
    throw new Error('For optimisation purposes you must install "react-easy-crop" ' + 'and pass it in via the `reactEasyCrop` prop of either this, or the root Form component.');
  }
  const Cropper = reactEasyCrop;
  const imageInputRef = useRef();
  const containerRef = useRef();

  // Load image data from the JSON-stringified image object that may have been
  // passed in (i.e. when editing a previously stored image).
  const defaultImgData = getDefaultImgData(defaultValue || (defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues[name]));
  const [imageData, setImageData] = useState(defaultImgData);
  const [currentImageData, setCurrentImageData] = useState({});
  const [initialCroppedAreaPixels, setInitialCroppedAreaPixels] = useState();
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({
    x: 0,
    y: 0
  });
  const [imageSrc, setImageSrc] = useState('');
  const [isCropping, setIsCropping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isTouchEnabled, setIsTouchEnabled] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  // Set width and height of the image containers.
  const imgAspectRatio = imageSizes[aspectRatio] || aspectRatio;
  const containerHeight = imgAspectRatio * containerWidth;
  const imgStyle = {
    maxWidth: containerWidth,
    height: containerHeight
  };

  /**
   * Set an error, return false and remove value from file input.
   */
  const raiseError = message => {
    if (validateInput && message) {
      imageInputRef.current.setCustomValidity(message);
      validateInput({
        target: imageInputRef.current
      });
    }
    if (imageInputRef.current) {
      imageInputRef.current.value = null;
    }
    return false;
  };

  /**
   * Validate an image based on rules defined in props.
   */
  const validateImage = (image, file) => {
    const {
      height,
      width
    } = image;
    const {
      size
    } = file;
    const maxSize = (maxFileSize / 1000000).toFixed(2);
    if (maxFileSize < size) {
      return raiseError(t('molecules.formImageUpload.error.maxFileSize', `Filesize is too large, please upload an image smaller than ${maxSize}MB`, {
        maxFileSize: maxSize
      }));
    }
    if (height < minDimensions.height) {
      return raiseError(t('molecules.formImageUpload.error.minHeight', `Image does not meet the minimum height requirement of ${minDimensions.height}px`, {
        minDimensions: minDimensions.height
      }));
    }
    if (width < minDimensions.width) {
      return raiseError(t('molecules.formImageUpload.error.minWidth', `Image does not meet the minimum width requirement of ${minDimensions.width}px`, {
        minDimensions: minDimensions.width
      }));
    }
    if (errors.image) {
      imageInputRef.current.setCustomValidity('');
      validateInput({
        target: imageInputRef.current
      });
    }
    return true;
  };

  /**
   * Convert a crop array to an object.
   */
  const getCropObjFromImageData = (data = {}) => {
    const {
      crop: cropArr
    } = data;
    if (!Array.isArray(cropArr)) {
      return {};
    }
    return {
      x: cropArr[0],
      y: cropArr[1],
      width: cropArr[2],
      height: cropArr[3]
    };
  };

  /**
   * Get the zoom level for the cropper from an image data object.
   */
  const getZoomFromImageData = (data = {}) => {
    var _data$crop, _data$crop2;
    if (!data.height || !data.crop) {
      return 1;
    }
    const landscape = data.width / data.height > 1;
    const zoomOrientation = landscape ? data.height / ((_data$crop = data.crop) === null || _data$crop === void 0 ? void 0 : _data$crop[3]) : data.width / ((_data$crop2 = data.crop) === null || _data$crop2 === void 0 ? void 0 : _data$crop2[2]);
    return zoomOrientation;
  };

  /**
   * Save the image data.
   *
   * The `imageData` is what ultimately gets sent to, for example, our API
   * services. The `currentImageData` stores the state of the image while
   * cropping.
   */
  const save = () => {
    setZoom(getZoomFromImageData(currentImageData));
    setImageData({
      ...currentImageData
    });
    setIsCropping(false);
  };

  /**
   * Clear the current image.
   */
  const clearImage = () => {
    setImageSrc(null);
    setImageData(null);
    setInitialCroppedAreaPixels(null);
    setZoom(1);
    setIsCropping(false);

    // Clear the input otherwise so we can load the same image multiple times
    // (there would be no change detected otherwise).
    imageInputRef.current.value = '';
  };

  /**
   * Load image uploaded from user.
   */
  const loadImage = async () => {
    const file = imageInputRef.current.files[0];
    const imageDataUrl = await readAsDataURL(file);
    const image = new Image();
    image.onload = () => {
      if (validateImage(image, file)) {
        const newImageData = {
          ...imageData,
          width: image.width,
          height: image.height
        };
        setImageData(newImageData);
        setCurrentImageData(newImageData);
        setIsCropping(true);
        setImageSrc(imageDataUrl);
      }
    };
    image.src = imageDataUrl;
  };

  /**
   * Create an image and wait for it to load.
   */
  const createImage = async url => {
    const image = new Image();
    return new Promise((resolve, reject) => {
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', err => reject(err));
      image.src = url;
    });
  };

  /**
   * Apply any image rotation.
   *
   * Generally, we want to store the original image data and let our image
   * service handle any modifications to that image. However, as our current
   * image service does not provide a rotation option we have to modify the
   * original image instead.
   */
  const getRotatedImage = async (imgSrc, imgRotation) => {
    const image = await createImage(imgSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = image.height;
    canvas.height = image.width;

    // Move to the center of the canvas.
    ctx.translate(canvas.width / 2, canvas.height / 2);

    // Rotate to the required degrees.
    ctx.rotate(imgRotation * Math.PI / 180);

    // Draw the rotated image.
    ctx.drawImage(image, -image.width * 0.5, -image.height * 0.5);
    return canvas.toDataURL('image/jpeg');
  };

  /**
   * Set the crop dimensions (x, y, width and height).
   */
  const onCropComplete = useCallback((area, areaPixels) => {
    setCurrentImageData({
      ...currentImageData,
      crop: [areaPixels.x, areaPixels.y, areaPixels.width, areaPixels.height]
    });

    // Clear the initial crop and let the cropper handle it from here. Things
    // jump around strangely otherwise.
    setInitialCroppedAreaPixels(null);
  }, [currentImageData]);

  /**
   * Rotate the original image.
   *
   * As we only ever rotate by 90 degrees (negative or positive) we can rotate
   * the original, feed it back into the cropper, and when the crop is complete
   * the parameters we get back will be based on that rotated image.
   */
  const rotate = async degrees => {
    const rotatedImageSrc = await getRotatedImage(imageSrc, degrees);
    const newImageData = {
      ...currentImageData,
      width: currentImageData.height,
      height: currentImageData.width
    };
    setImageSrc(rotatedImageSrc);
    setImageData(newImageData);
    setCurrentImageData(newImageData);
  };

  // Detect if the device using the component has touch controls.
  useEffect(() => {
    setIsTouchEnabled(window.matchMedia('(hover: none), (pointer: coarse)').matches);
  }, []);

  // Use a resize observer to set the cropper width to fit its container.
  // The polyfill supports IE >= 9.
  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      entries.forEach(({
        contentRect
      }) => {
        setContainerWidth(contentRect.width);
      });
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  // Load any image passed in via an image object.
  useEffect(() => {
    (async () => {
      if (!imageData.url) {
        setLoading(false);
        return;
      }
      const res = await fetch(imageData.url);
      const blob = await res.blob();
      const dataUrl = await readAsDataURL(blob);
      setImageSrc(dataUrl);
      if (imageData.width && imageData.crop) {
        setZoom(getZoomFromImageData(imageData));
      }
      if (imageData.crop) {
        setInitialCroppedAreaPixels(getCropObjFromImageData(imageData));
      }
      setCurrentImageData({
        ...imageData
      });
      setLoading(false);
    })();
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: cn('form-image-upload', className, errors && name in errors && 'form-image-upload--invalid', inverted && 'form-image-upload--inverted'),
    ref: containerRef
  }, imageSrc && !isCropping && /*#__PURE__*/React.createElement("div", {
    style: imgStyle,
    className: "p-relative"
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => setIsCropping(true),
    onKeyDown: () => setIsCropping(true),
    type: "button"
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: imageData.width / ((_imageData$crop = imageData.crop) === null || _imageData$crop === void 0 ? void 0 : _imageData$crop[2]) * containerWidth || 0,
      height: imageData.height / ((_imageData$crop2 = imageData.crop) === null || _imageData$crop2 === void 0 ? void 0 : _imageData$crop2[3]) * containerHeight || 0
    }
  }, /*#__PURE__*/React.createElement("img", {
    alt: "",
    src: imageSrc,
    className: "p-relative full-width",
    style: {
      left: -(((_imageData$crop3 = imageData.crop) === null || _imageData$crop3 === void 0 ? void 0 : _imageData$crop3[0]) / ((_imageData$crop4 = imageData.crop) === null || _imageData$crop4 === void 0 ? void 0 : _imageData$crop4[2]) * containerWidth) || 0,
      top: -(((_imageData$crop5 = imageData.crop) === null || _imageData$crop5 === void 0 ? void 0 : _imageData$crop5[1]) / ((_imageData$crop6 = imageData.crop) === null || _imageData$crop6 === void 0 ? void 0 : _imageData$crop6[3]) * containerHeight) || 0
    }
  }))), /*#__PURE__*/React.createElement("button", {
    className: "form-image-upload__remove ma-sm",
    onClick: clearImage,
    onKeyDown: clearImage,
    type: "button"
  }, _ref)), isCropping && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "p-relative form-image-upload__crop-container",
    style: {
      paddingBottom: imgStyle.height,
      width: imgStyle.maxWidth
    }
  }, /*#__PURE__*/React.createElement(Cropper, {
    aspect: 1 / imgAspectRatio,
    crop: crop,
    initialCroppedAreaPixels: initialCroppedAreaPixels,
    image: imageSrc,
    zoom: zoom,
    zoomWithScroll: false,
    onCropChange: setCrop,
    onCropComplete: onCropComplete,
    onZoomChange: setZoom
  })), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-direction-column mt-sm mb-xs"
  }, !isTouchEnabled && /*#__PURE__*/React.createElement(FormRange, {
    topLabel: "Zoom",
    min: 1,
    max: 3,
    name: `${name}-zoom`,
    step: 0.1,
    defaultValue: zoom,
    onChange: setZoom
  }), /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-center mt-sm mb-sm"
  }, /*#__PURE__*/React.createElement(Button, {
    small: true,
    link: true,
    className: "mr-xs ml-xs",
    onClick: () => rotate(-90),
    noMinWidth: true
  }, t('molecules.formImageUpload.button.rotateLeft')), /*#__PURE__*/React.createElement(Button, {
    small: true,
    link: true,
    className: "mr-xs ml-xs",
    onClick: () => rotate(90),
    noMinWidth: true
  }, t('molecules.formImageUpload.button.rotateRight')))), /*#__PURE__*/React.createElement("div", {
    className: 'd-flex justify-content-center' + (isTouchEnabled ? " pt-sm" : "")
  }, /*#__PURE__*/React.createElement(Button, {
    secondary: true,
    className: "mr-xs ml-xs",
    onClick: clearImage,
    noMinWidth: true
  }, t('molecules.formImageUpload.button.remove')), /*#__PURE__*/React.createElement(Button, {
    className: "mr-xs ml-xs",
    onClick: save,
    noMinWidth: true
  }, t('molecules.formImageUpload.button.save'))))), !imageSrc && /*#__PURE__*/React.createElement("div", {
    className: "p-relative",
    style: imgStyle
  }, /*#__PURE__*/React.createElement(DashedButton, {
    className: "form-image-upload__button",
    onClick: () => {
      imageInputRef.current.click();
    }
  }, !loading && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "pt-sm"
  }, /*#__PURE__*/React.createElement(Icon, {
    className: "ma-auto",
    name: "camera",
    color: inverted ? 'baseWhite' : 'backgroundRegular',
    size: 60
  }), /*#__PURE__*/React.createElement("p", {
    className: "form-image-upload__button-text mb-lg mt-sm body-copy-bold"
  }, t('molecules.formImageUpload.button.loadPrompt'), ' ', required ? `(${t('molecules.formImageUpload.button.loadPromptRequired', 'required')})` : `(${t('molecules.formImageUpload.button.loadPromptOptional', 'optional')})`)), /*#__PURE__*/React.createElement("p", {
    className: "form-image-upload__button-text ma-reset body-copy-extra-small"
  }, t('molecules.formImageUpload.button.ownImage', '*Please ensure this is your own image'))))), /*#__PURE__*/React.createElement("input", _extends({}, restProps, {
    required: required,
    accept: accept,
    className: "hidden",
    name: name,
    ref: imageInputRef,
    id: inputId,
    "aria-describedby": feedbackId,
    type: "file",
    onChange: loadImage
  })), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: `${name}-src`,
    defaultValue: imageSrc
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: `${name}-data`,
    defaultValue: JSON.stringify(imageData)
  }));
};
FormImageUpload.defaultProps = {
  className: null,
  accept: 'image/*',
  defaultValue: null,
  aspectRatio: 'square',
  minDimensions: {
    height: 500,
    width: 500
  },
  maxFileSize: 15000000,
  required: false,
  name: null,
  reactEasyCrop: null
};
FormImageUpload.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** Aspect ratio of the crop. */
  aspectRatio: oneOfType([number, string]),
  /** Minimum dimensions of the image that is being uploaded */
  minDimensions: shape({
    height: number,
    width: number
  }),
  /** Maximum filesize of the image being uploaded default is 15MB */
  maxFileSize: number,
  /** Whether the image is a required field. */
  required: bool,
  /** Can be passed to allow for a specific set of extensions IE 'image/jpg, image/gif' */
  accept: string,
  /** A JSON-stringified object containing the image data. */
  defaultValue: oneOfType([string, object]),
  /** The input name. */
  name: oneOfType([number, string]),
  /** The react-easy-crop library. */
  reactEasyCrop: func
} : {};
export default FormImageUpload;